import { PATH } from "constants/path"
import { setTabCurrentProjectEncryptionKey } from "controllers/EncryptionController"
import { runtimeKeyStore } from "stores/runtimeKeyStore"
import { pushTo } from "helpers/history"
import { getPrivateKeyByUserId } from "helpers/utils"
import { isEmpty } from "lodash"
import {
  getUnreadBuildMiddleware,
  getUnreadConversationMiddleware,
} from "pages/auth/services/api"
import {
  acceptedSharingProjectMiddleware,
  createEncryptionKeyMiddleware,
} from "pages/projects/services/api"
import { ProjectDetail } from "pages/projects/types"
import { KeyEncryptionType } from "types"
import { useAppSelector } from "./useApp"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { getUserVaultKey, removeSharedProject } from "helpers/local_storage"
import {
  generateTRCCode,
  captureShareEvent,
  addBreadcrumb,
  getSentryUserContext,
} from "helpers/sentryMonitoring"
import { umamiTracking } from "helpers/utils"
import { EVENT } from "constants/events"

export const useShareInvitation = () => {
  const userInfo = useAppSelector((state) => state.userInfo)

  const acceptedSharingProject = async (
    projectShare: ProjectDetail,
    isRedirectProjectComponent = false
  ): Promise<boolean> => {
    addBreadcrumb("share", "Accept invitation clicked", "info", {
      project_id: projectShare.id,
    })
    try {
      if (projectShare.isProjectKeyEncrypted) {
        if (userInfo.id !== projectShare.created_by) {
          acceptedSharingProjectMiddleware(projectShare.id)
        }
        if (isRedirectProjectComponent) {
          pushTo(PATH.projectComponent, {
            idProject: projectShare.id,
          })
        }
        return true
      }
      const userVaultKey = getUserVaultKey()
      //For project sharing
      const projectKey = projectShare.encryptedKey
      if (!projectKey) {
        if (isRedirectProjectComponent) {
          pushTo(PATH.projects)
        }
        return false
      }
      addBreadcrumb(
        "share",
        "Fetching project share — decrypt key attempt",
        "info",
        {
          project_id: projectShare.id,
        }
      )
      try {
        const privateKeyPem: any = await getPrivateKeyByUserId()
        const decryptProjectKeyByPrivateKey = privateKeyPem.decrypt(
          projectKey,
          "RSA-OAEP"
        )
        if (decryptProjectKeyByPrivateKey) {
          runtimeKeyStore.upsertProjectKeys({
            [projectShare.id]: decryptProjectKeyByPrivateKey,
          })
          setTabCurrentProjectEncryptionKey(
            isEmpty(decryptProjectKeyByPrivateKey)
              ? ""
              : (decryptProjectKeyByPrivateKey as string)
          )

          const ownerEncryptedKey = await encryptionGCMHelper.encryptContext(
            userVaultKey,
            decryptProjectKeyByPrivateKey
          )

          await createEncryptionKeyMiddleware({
            encrypted_key: ownerEncryptedKey,
            model: "projects",
            relation_id: projectShare.id,
            type: KeyEncryptionType.ModuleKey,
          })
        }

        await acceptedSharingProjectMiddleware(projectShare.id)
        umamiTracking(EVENT.KEY_DECRYPT.SHARE_SUCCESS, {})
        if (isRedirectProjectComponent) {
          removeSharedProject()
          getUnreadBuildMiddleware()
          getUnreadConversationMiddleware()
          pushTo(PATH.projectComponent, {
            idProject: projectShare.id,
          })
        }
        return true
      } catch {
        umamiTracking(EVENT.KEY_DECRYPT.SHARE_FAILURE, { error: "decrypt_share_key" })
        addBreadcrumb(
          "share",
          "Decrypt project key by private key failed",
          "warning"
        )
        const code = generateTRCCode("SHR")
        captureShareEvent(
          `Error decrypt project key by private key - ${code}`,
          {
            error_category: "SHR",
            error_type: "DECRYPT_PROJECT_KEY_FAILURE",
            ...getSentryUserContext(),
            error_code: code,
            step_trail:
              "useShareInvitation → acceptedSharingProject → decrypt by private key",
            project_id: projectShare.id,
          }
        )
        return false
      }
    } catch {
      umamiTracking(EVENT.KEY_DECRYPT.SHARE_FAILURE, {
        error: "accepted_sharing_outer",
      })
      const code = generateTRCCode("SHR")
      captureShareEvent(`Error accepted sharing project - ${code}`, {
        error_category: "SHR",
        error_type: "ACCEPT_SHARING_FAILURE",
        ...getSentryUserContext(),
        error_code: code,
        step_trail: "useShareInvitation → acceptedSharingProject → outer catch",
        project_id: projectShare.id,
      })
      return false
    }
  }

  return { acceptedSharingProject }
}
